import { PLACE_ID_PATTERN } from '@bandido/shared/types';

import type { EsimsSearchPayload, PricedSearchResult } from './queries';

/**
 * Helpers del vertical de eSIMs.
 *
 * El orden vive acá y no dentro de la tabla porque la lista se dibuja de dos
 * formas —tabla en desktop, cards apiladas en mobile— y las dos tienen que
 * ordenar IGUAL: si el criterio vive en una de las dos vistas, la otra ordena
 * distinto o directamente no ordena.
 */

/** Por qué se puede comparar un paquete contra otro. */
export const ESIM_SORT_KEYS = ['price', 'data', 'validity'] as const;

export type EsimSortKey = (typeof ESIM_SORT_KEYS)[number];

/** Reconstruye solo países canónicos (`plc_*`) o regiones de Rutero. */
export function esimsSearchPayloadFromParams(
  params: Record<string, string>,
): EsimsSearchPayload | null {
  if (params.buscar !== '1') return null;
  if (
    params.tipo === 'local' &&
    params.destino &&
    PLACE_ID_PATTERN.test(params.destino)
  ) {
    return { destination: params.destino, type: 'local' };
  }
  if (params.tipo === 'global' && params.region) {
    return { region: params.region, type: 'global' };
  }
  return null;
}

/** GB del paquete para ordenar/filtrar. Ilimitado = Infinity. */
export function parseDataGb(result: PricedSearchResult): number {
  if (result.unlimited) return Number.POSITIVE_INFINITY;
  const raw = result.dataAmount ?? '';
  const match = /([\d.,]+)\s*(GB|MB)/i.exec(raw);
  if (!match) return 0;
  const amount = Number(match[1].replace(',', '.'));
  if (Number.isNaN(amount)) return 0;
  return match[2].toUpperCase() === 'MB' ? amount / 1024 : amount;
}

function sortValue(result: PricedSearchResult, key: EsimSortKey): number {
  if (key === 'price') return result.pricing.final.amountMinor;
  if (key === 'data') return parseDataGb(result);
  return result.validityDays ?? 0;
}

/**
 * Los paquetes ordenados. No muta la lista recibida: es la misma referencia
 * que filtra el buscador, y ordenarla en el lugar reordenaría también lo que
 * ve el resto de la pantalla.
 */
export function sortEsims(
  results: PricedSearchResult[],
  key: EsimSortKey,
  ascending: boolean,
): PricedSearchResult[] {
  return [...results].sort((a, b) => {
    const diff = sortValue(a, key) - sortValue(b, key);
    // `Infinity - Infinity` es NaN: dos paquetes ilimitados son equivalentes
    // para el orden, no un resultado indefinido que rompa el comparador.
    const resolved = Number.isNaN(diff) ? 0 : diff;
    return ascending ? resolved : -resolved;
  });
}
